import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import { api, ApiError } from './api';
import { htmlLogoBranding } from './branding';
import { prepararVistaPreviaTicket } from './vistaPreviaTicket';

export default function HistorialVentas({ usuario }) {
  const [ventas, setVentas] = useState([]);
  const [ticketViendo, setTicketViendo] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [usuarioFiltro, setUsuarioFiltro] = useState('');
  const [modalDevolucion, setModalDevolucion] = useState(null);
  const [cantidadesDevolucion, setCantidadesDevolucion] = useState({});
  const [motivoDevolucion, setMotivoDevolucion] = useState('');
  const [supervisorUsuario, setSupervisorUsuario] = useState('');
  const [supervisorPin, setSupervisorPin] = useState('');
  const [procesandoDevolucion, setProcesandoDevolucion] = useState(false);
  
  const hoy = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0];
  const [fechaInicio, setFechaInicio] = useState(hoy);
  const [fechaFin, setFechaFin] = useState(hoy);

  useEffect(() => {
    api.get('/api/usuarios')
      .then(data => setUsuarios(Array.isArray(data) ? data : []))
      .catch(err => console.error("Error al cargar usuarios", err));
  }, []);

  const cargarVentas = async () => {
    if (!fechaInicio || !fechaFin) return alert("Por favor seleccioná ambas fechas.");
    try {
      let path = `/api/ventas?inicio=${fechaInicio}&fin=${fechaFin}&t=${Date.now()}`;
      if (usuarioFiltro) path += `&usuario_id=${usuarioFiltro}`;
      const data = await api.get(path);
      setVentas(Array.isArray(data) ? data : []);
    } catch (err) {
      alert("❌ " + (err instanceof ApiError ? err.mensaje : 'Error de conexión con el backend.'));
    }
  };

  useEffect(() => { 
    cargarVentas(); 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Descarga de archivos: usamos api.download para que viaje el token JWT
  // y además se valide el status, en vez de window.open que abriría una
  // página de error si el servidor responde con 401/500.
  const descargarArchivo = async (path, nombreArchivo) => {
    try {
      const blob = await api.download(path);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = nombreArchivo;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("❌ " + (err instanceof ApiError ? err.mensaje : 'No se pudo descargar el archivo.'));
    }
  };

  const exportarExcel = () => {
    let path = `/api/ventas/exportar/excel?inicio=${fechaInicio}&fin=${fechaFin}`;
    if (usuarioFiltro) path += `&usuario_id=${usuarioFiltro}`;
    descargarArchivo(path, `Ventas_${fechaInicio}_${fechaFin}.xlsx`);
  };

  const exportarPDF = () => {
    let path = `/api/ventas/exportar/pdf?inicio=${fechaInicio}&fin=${fechaFin}`;
    if (usuarioFiltro) path += `&usuario_id=${usuarioFiltro}`;
    descargarArchivo(path, `Ventas_${fechaInicio}_${fechaFin}.pdf`);
  };

  const verTicket = async (id) => {
    try {
      const data = await api.get(`/api/ventas/${id}?t=${Date.now()}`);
      setTicketViendo(data);
    } catch (err) { 
      alert("❌ " + (err instanceof ApiError ? err.mensaje : 'Error cargando detalle del ticket.'));
    }
  };

  const emitirFacturaAFIP = async (id) => {
    if (!window.confirm("¿Estás seguro de emitir una Factura en AFIP para esta venta? Esta acción es legal y no se puede deshacer.")) return;
    try {
      alert("⏳ Conectando con AFIP... Por favor esperá, no cierres la ventana.");
      const data = await api.post('/api/afip/facturar', { venta_id: id });
      if (data.success) {
        cargarVentas(); 
        verTicket(id);
      } else {
        alert(`❌ RECHAZO DE AFIP:\n${data.error}`);
      }
    } catch (err) {
      // El backend devuelve los rechazos de AFIP con un mensaje descriptivo
      // (ej. "Rechazo de AFIP: CUIT inválido"), que vale la pena mostrar.
      alert("❌ " + (err instanceof ApiError ? err.mensaje : 'Error de red al conectar con AFIP.'));
    }
  };

  const imprimirTicket = async (parametro) => {
      try {
          let ventaId;
          
          // 🛡️ BLINDAJE: Verificamos de forma segura el ID usando la variable correcta (ticketViendo)
          if (parametro && parametro.nativeEvent) {
              ventaId = ticketViendo ? ticketViendo.ticket.id : null;
          } else {
              ventaId = typeof parametro === 'object' && parametro !== null ? parametro.id : parametro;
          }

          if (!ventaId) {
              return alert("No se pudo detectar el ID del ticket para imprimir.");
          }

          let config = {};
          try { config = await api.get('/api/configuracion'); } catch { /* el ticket se puede imprimir sin la config */ }

          const dataVenta = await api.get(`/api/ventas/${ventaId}`);
          const ticket = dataVenta.ticket;
          const items = dataVenta.items || [];

          const ventana = window.open('', '', 'width=760,height=900');
          if (!ventana) {
              return alert("⚠️ Tu navegador bloqueó el ticket. Por favor, permití las ventanas emergentes (Pop-ups) arriba a la derecha.");
          }
          
          ventana.document.write(`
              <html>
                  <head>
                      <title>Ticket #${ticket.id}</title>
                      <style>
                          @page { margin: 0; }
                          body { font-family: 'Courier New', Courier, monospace; font-size: 12px; margin: 0; padding: 10px; width: 58mm; color: #000; }
                          .header { text-align: center; margin-bottom: 10px; }
                          .header h1 { font-size: 16px; margin: 0; text-transform: uppercase; }
                          .header p { margin: 2px 0; font-size: 10px; }
                          .divisor { border-top: 1px dashed #000; margin: 5px 0; }
                          table { width: 100%; border-collapse: collapse; font-size: 11px; }
                          th { text-align: left; border-bottom: 1px dashed #000; padding-bottom: 3px; }
                          td { padding: 3px 0; vertical-align: top; }
                          .cant { width: 15%; text-align: center; }
                          .desc { width: 55%; text-align: left; }
                          .precio { width: 30%; text-align: right; }
                          .totales { margin-top: 10px; text-align: right; }
                          .totales h2 { font-size: 16px; margin: 5px 0; }
                          .footer { text-align: center; margin-top: 15px; font-size: 10px; }
                      </style>
                  </head>
                  <body>
                      <div class="header">
                          ${htmlLogoBranding(config, { maxWidth: 150, maxHeight: 60 })}<h1>${config.nombre_negocio || 'KIOSCO POS'}</h1>
                          <p>CUIT: ${config.cuit || '00-00000000-0'}</p>
                          <div class="divisor"></div>
                          <p>TICKET NO FISCAL (COPIA)</p>
                          <p>Nro: ${ticket.ticket_serie || '0001'}-${String(ticket.ticket_numero || ticket.id).padStart(8, '0')}</p>
                          <p>${new Date(ticket.fecha).toLocaleString('es-AR')}</p>
                      </div>
                      
                      <div class="divisor"></div>
                      
                      <table>
                          <thead>
                              <tr>
                                  <th class="cant">Cant</th>
                                  <th class="desc">Detalle</th>
                                  <th class="precio">Subt</th>
                              </tr>
                          </thead>
                          <tbody>
                              ${items.map(item => `
                                  <tr>
                                      <td class="cant">${Number(item.cantidad)}</td>
                                      <td class="desc">
                                        ${item.descripcion ? item.descripcion.substring(0, 15) : 'Producto'}
                                        ${item.promocion_nombre ? `<br/><small><i>(${item.promocion_nombre})</i></small>` : ''}
                                      </td>
                                      <td class="precio">
                                         $${parseFloat(item.precio_original * item.cantidad).toFixed(2)}
                                         ${parseFloat(item.descuento_aplicado) > 0 ? `<br/><span style="color:#666">-$${parseFloat(item.descuento_aplicado).toFixed(2)}</span>` : ''}
                                      </td>
                                  </tr>
                              `).join('')}
                          </tbody>
                      </table>
                      
                      <div class="divisor"></div>
                      
                      <div class="totales">
                          <p>Subtotal: $${parseFloat(ticket.subtotal_original || ticket.total).toFixed(2)}</p>
                          ${parseFloat(ticket.descuento_total || 0) - parseFloat(ticket.descuento_manual_total || 0) > 0 ? `<p style="color:#ef4444; font-weight:bold;">Ahorro Promos: -$${(parseFloat(ticket.descuento_total || 0) - parseFloat(ticket.descuento_manual_total || 0)).toFixed(2)}</p>` : ''}
                          ${parseFloat(ticket.descuento_manual_total || 0) > 0 ? `<p style="color:#ef4444; font-weight:bold;">Descuento manual: -$${parseFloat(ticket.descuento_manual_total).toFixed(2)}</p>` : ''}
                          <h2>TOTAL: $${parseFloat(ticket.total).toFixed(2)}</h2>
                          <p>Pago: ${ticket.metodo_pago || ticket.forma_pago}</p>
                          <p>Cliente: ${ticket.cliente_nombre || 'Consumidor Final'}</p>
                      </div>
                      
                      <div class="footer">
                          <p>Copia reimpresa del original</p>
                      </div>
                  </body>
              </html>
          `);
          ventana.document.close();
          ventana.focus();
          
          prepararVistaPreviaTicket(ventana);

      } catch (err) {
          console.error("Error al imprimir:", err);
          alert("Error de conexión al generar el ticket. Mirá la consola para más detalles.");
      }
  };

  const abrirDevolucion = (tipo) => {
    if (!ticketViendo) return;
    const cantidades = {};
    (ticketViendo.items || []).forEach((item) => {
      const disponible = Number(item.cantidad_disponible_devolucion || 0);
      cantidades[item.id] = tipo === 'Total' ? disponible : 0;
    });
    setCantidadesDevolucion(cantidades);
    setMotivoDevolucion('');
    setSupervisorUsuario('');
    setSupervisorPin('');
    setModalDevolucion({ tipo });
  };

  const confirmarDevolucion = async () => {
    if (!ticketViendo || !modalDevolucion || procesandoDevolucion) return;
    const items = (ticketViendo.items || [])
      .map((item) => ({ detalle_id: item.id, cantidad: Number.parseFloat(cantidadesDevolucion[item.id]) || 0 }))
      .filter((item) => item.cantidad > 0);
    if (modalDevolucion.tipo === 'Parcial' && !items.length) return alert('Seleccioná al menos una cantidad para devolver.');
    if (motivoDevolucion.trim().length < 3) return alert('Ingresá el motivo de la devolución.');
    const idempotencyKey = window.crypto?.randomUUID?.() || `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setProcesandoDevolucion(true);
    try {
      const data = await api.post(`/api/ventas/${ticketViendo.ticket.id}/devoluciones`, {
        tipo: modalDevolucion.tipo,
        items,
        motivo: motivoDevolucion,
        idempotency_key: idempotencyKey,
        autorizacion: { usuario: supervisorUsuario, pin: supervisorPin },
      });
      alert(`✅ Operación registrada. Reintegro: $${Number(data.total_reintegrado || 0).toFixed(2)}
Estado: ${data.estado_venta}`);
      setModalDevolucion(null);
      await cargarVentas();
      await verTicket(ticketViendo.ticket.id);
    } catch (err) {
      alert('❌ ' + (err instanceof ApiError ? err.mensaje : 'No se pudo registrar la devolución.'));
    } finally { setProcesandoDevolucion(false); }
  };

  const estadoColor = (estado) => estado === 'Anulada' ? '#dc2626' : estado === 'Parcialmente devuelta' ? '#b45309' : '#15803d';

  const totalFiltrado = ventas.reduce((sum, v) => sum + parseFloat(v.total) - parseFloat(v.total_devuelto || 0), 0);

  return (
    <div className="dash-container">
      <div className="card-panel" style={{ marginBottom: '0', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: '20px' }}>
        <div>
          <h2 style={{ margin: 0, color: '#0f172a', fontSize: '24px' }}>🧾 Historial de Ventas</h2>
        </div>
        
        <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={labelStyle}>Cajero:</label>
            <select value={usuarioFiltro} onChange={e => setUsuarioFiltro(e.target.value)} style={inputStyle}>
              <option value="">Todos los cajeros</option>
              {usuarios.map(u => (
                <option key={u.id} value={u.id}>{u.username}</option>
              ))}
            </select>
          </div>
          <div><label style={labelStyle}>Desde:</label><input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} style={inputStyle} /></div>
          <div><label style={labelStyle}>Hasta:</label><input type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)} style={inputStyle} /></div>
          
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={cargarVentas} style={{ ...btnStyle, backgroundColor: '#3b82f6', color: 'white' }}>🔍 Filtrar</button>
            <button onClick={exportarExcel} style={{ ...btnStyle, backgroundColor: '#10b981', color: 'white' }}>🟢 Excel</button>
            <button onClick={exportarPDF} style={{ ...btnStyle, backgroundColor: '#ef4444', color: 'white' }}>🔴 PDF</button>
          </div>
        </div>
      </div>

      <div className="card-panel">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3 className="card-title" style={{ margin: 0 }}>Resultados ({ventas.length} tickets)</h3>
          <h3 style={{ margin: 0, color: '#10b981' }}>Total neto: ${totalFiltrado.toFixed(2)}</h3>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="table-modern">
            <thead>
              <tr>
                <th>Comprobante</th>
                <th>Fecha y Hora</th>
                <th>Cajero</th>
                <th>Cliente</th>
                <th>Medio de Pago</th>
                <th>Estado</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ textAlign: 'center' }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {ventas.map(v => (
                <tr key={v.id}>
                  <td style={{ fontWeight: 'bold', color: '#0f172a' }}>
                    {v.cae ? <span style={{color: '#10b981'}}>✔ FC-{String(v.factura_num).padStart(5, '0')}</span> : `${v.ticket_serie || '0001'}-${String(v.ticket_numero || v.id).padStart(8, '0')}`}
                  </td>
                  <td style={{ color: '#64748b' }}>{new Date(v.fecha).toLocaleDateString('es-AR')}</td>
                  <td style={{ color: '#475569', fontWeight: '600', textTransform: 'capitalize' }}>{v.cajero || 'Sistema'}</td>
                  <td style={{ color: '#475569' }}>{v.cliente_nombre || 'Consumidor Final'}</td>
                  <td>
                    <span style={{ backgroundColor: v.metodo_pago === 'Efectivo' ? '#d1fae5' : '#f3e8ff', color: v.metodo_pago === 'Efectivo' ? '#065f46' : '#6b21a8', padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold' }}>
                      {v.metodo_pago || v.forma_pago}
                    </span>
                  </td>
                  <td><span style={{ color: estadoColor(v.estado), fontWeight: 'bold', fontSize: '12px' }}>{v.estado || 'Confirmada'}</span></td>
                  <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#0f172a' }}>${parseFloat(v.total).toFixed(2)}{Number(v.total_devuelto || 0) > 0 && <div style={{ fontSize: '11px', color: '#dc2626' }}>Devuelto: -${Number(v.total_devuelto).toFixed(2)}</div>}</td>
                  <td style={{ textAlign: 'center' }}>
                    <button onClick={() => verTicket(v.id)} style={{ backgroundColor: '#3b82f6', color: 'white', padding: '6px 12px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
                      🔍 Ver
                    </button>
                  </td>
                </tr>
              ))}
              {ventas.length === 0 && <tr><td colSpan="8" style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>No hay ventas registradas.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {ticketViendo && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(15, 23, 42, 0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', width: '440px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <h3 style={{ textAlign: 'center', margin: '0 0 5px 0', paddingBottom: '5px' }}>
              Detalle Venta #{ticketViendo.ticket.id}
            </h3>
            <p style={{ textTransform: 'capitalize', textAlign: 'center', color: '#64748b', fontSize: '13px', margin: '0 0 8px 0' }}>
              👤 Cajero: <strong>{ticketViendo.ticket.cajero || 'Sistema'}</strong>
            </p>
            <p style={{ textAlign: 'center', margin: '0 0 15px 0', borderBottom: '1px dashed #cbd5e1', paddingBottom: '10px', color: estadoColor(ticketViendo.ticket.estado), fontWeight: 'bold' }}>
              {ticketViendo.ticket.estado || 'Confirmada'}
            </p>
            
            <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '15px' }}>
              <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse' }}>
                <tbody>
                  {ticketViendo.items.map((item, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 0', color: '#475569' }}>{Number(item.cantidad)}x {item.descripcion}{Number(item.cantidad_devuelta || 0) > 0 && <div style={{ fontSize: '11px', color: '#dc2626' }}>Devuelto: {Number(item.cantidad_devuelta)}</div>}</td>
                      <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 'bold', color: '#0f172a' }}>${parseFloat(item.subtotal).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc', padding: '10px', borderRadius: '8px' }}>
              <span style={{ color: '#64748b', fontWeight: 'bold' }}>TOTAL:</span>
              <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#10b981' }}>${parseFloat(ticketViendo.ticket.total).toFixed(2)}</span>
            </div>

            {Array.isArray(ticketViendo.pagos) && ticketViendo.pagos.length > 0 && (
              <div style={{ marginTop: '10px', padding: '10px', background: '#f8fafc', borderRadius: '8px', fontSize: '12px' }}>
                <strong>Pagos:</strong>
                {ticketViendo.pagos.map((p) => <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', marginTop: '3px' }}><span>{p.metodo_pago}</span><span>${Number(p.monto).toFixed(2)}</span></div>)}
                {Number(ticketViendo.ticket.vuelto || 0) > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', color: '#047857', fontWeight: 'bold' }}><span>Vuelto</span><span>${Number(ticketViendo.ticket.vuelto).toFixed(2)}</span></div>}
              </div>
            )}

            {Array.isArray(ticketViendo.devoluciones) && ticketViendo.devoluciones.length > 0 && (
              <div style={{ marginTop: '10px', padding: '10px', background: '#fff7ed', borderRadius: '8px', fontSize: '12px' }}>
                <strong>Posventa registrada:</strong>
                {ticketViendo.devoluciones.map((d) => <div key={d.id} style={{ marginTop: '5px', borderTop: '1px solid #fed7aa', paddingTop: '5px' }}>#{d.id} · {d.tipo} · ${Number(d.total_reintegrado).toFixed(2)}<br/><span style={{ color: '#64748b' }}>{d.motivo}</span></div>)}
              </div>
            )}

            {ticketViendo.ticket.cae ? (
              <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#ecfdf5', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
                <p style={{ margin: '0 0 8px 0', color: '#065f46', fontWeight: 'bold', fontSize: '14px', textAlign: 'center' }}>✅ FACTURA {ticketViendo.ticket.factura_tipo} EMITIDA</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#047857', marginBottom: '4px' }}><span>Factura Nro:</span> <strong>{String(ticketViendo.ticket.factura_num).padStart(8, '0')}</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#047857', marginBottom: '4px' }}><span>CAE:</span> <strong>{ticketViendo.ticket.cae}</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#047857' }}><span>Vto CAE:</span> <strong>{ticketViendo.ticket.cae_vto}</strong></div>
              </div>
            ) : (
              <button onClick={() => emitirFacturaAFIP(ticketViendo.ticket.id)} style={{ width: '100%', marginTop: '15px', padding: '12px', backgroundColor: '#0ea5e9', color: 'white', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', justifyContent: 'center', gap: '8px' }}>
                🏛️ Facturar en AFIP
              </button>
            )}

            {ticketViendo.ticket.estado !== 'Anulada' && (
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button disabled={Boolean(ticketViendo.ticket.cae)} onClick={() => abrirDevolucion('Parcial')} style={{ flex: 1, padding: '10px', background: ticketViendo.ticket.cae ? '#cbd5e1' : '#f59e0b', color: 'white', border: 0, borderRadius: '7px', fontWeight: 'bold', cursor: ticketViendo.ticket.cae ? 'not-allowed' : 'pointer' }}>↩️ Devolución parcial</button>
                <button disabled={Boolean(ticketViendo.ticket.cae)} onClick={() => abrirDevolucion('Total')} style={{ flex: 1, padding: '10px', background: ticketViendo.ticket.cae ? '#cbd5e1' : '#dc2626', color: 'white', border: 0, borderRadius: '7px', fontWeight: 'bold', cursor: ticketViendo.ticket.cae ? 'not-allowed' : 'pointer' }}>⛔ Anular venta</button>
              </div>
            )}
            {ticketViendo.ticket.cae && <p style={{ fontSize: '11px', color: '#b45309', textAlign: 'center', margin: '7px 0' }}>Las ventas facturadas requieren nota de crédito electrónica.</p>}

            <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
              <button onClick={() => setTicketViendo(null)} style={{ flex: 1, padding: '12px', backgroundColor: '#e2e8f0', color: '#475569', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Cerrar</button>
              {/* 🚀 ACÁ ASEGURAMOS QUE MANDE EL ID CORRECTO DIRECTAMENTE AL HACER CLIC */}
              <button onClick={() => imprimirTicket(ticketViendo.ticket.id)} style={{ flex: 1, padding: '12px', backgroundColor: '#0f172a', color: 'white', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>🖨️ Reimprimir</button>
            </div>
          </div>
        </div>
      )}

      {modalDevolucion && ticketViendo && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.78)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', width: '92%', maxWidth: '540px', maxHeight: '90vh', overflowY: 'auto', borderRadius: '12px', padding: '20px' }}>
            <h3 style={{ marginTop: 0 }}>{modalDevolucion.tipo === 'Total' ? '⛔ Anular venta completa' : '↩️ Devolución parcial'} #{ticketViendo.ticket.id}</h3>
            {modalDevolucion.tipo === 'Parcial' && (ticketViendo.items || []).map((item) => (
              <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: '10px', alignItems: 'center', borderBottom: '1px solid #e2e8f0', padding: '8px 0' }}>
                <div><strong>{item.descripcion}</strong><div style={{ fontSize: '12px', color: '#64748b' }}>Disponible: {Number(item.cantidad_disponible_devolucion || 0)}</div></div>
                <input type="number" min="0" max={Number(item.cantidad_disponible_devolucion || 0)} step="0.001" value={cantidadesDevolucion[item.id] || ''}
                  onChange={(e) => setCantidadesDevolucion({ ...cantidadesDevolucion, [item.id]: e.target.value })} style={{ padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px' }} />
              </div>
            ))}
            {modalDevolucion.tipo === 'Total' && <p style={{ padding: '10px', background: '#fef2f2', color: '#991b1b', borderRadius: '8px' }}>Se devolverán todas las cantidades restantes, se reingresará el stock y se reintegrará según los medios de pago originales.</p>}
            <label style={labelStyle}>Motivo obligatorio</label>
            <textarea value={motivoDevolucion} onChange={(e) => setMotivoDevolucion(e.target.value)} rows={3} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} placeholder="Ej.: producto defectuoso, error de cobro..." />
            {usuario?.rol?.toLowerCase() !== 'admin' && <div style={{ marginTop: '12px', padding: '10px', background: '#fff7ed', borderRadius: '8px' }}>
              <strong style={{ fontSize: '13px', color: '#b45309' }}>Autorización de supervisor</strong>
              <div style={{ display: 'flex', gap: '8px', marginTop: '7px' }}>
                <input value={supervisorUsuario} onChange={(e) => setSupervisorUsuario(e.target.value)} placeholder="Usuario admin" style={{ ...inputStyle, flex: 1 }} />
                <input type="password" inputMode="numeric" value={supervisorPin} onChange={(e) => setSupervisorPin(e.target.value)} placeholder="PIN" style={{ ...inputStyle, width: '100px' }} />
              </div>
            </div>}
            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button onClick={() => setModalDevolucion(null)} disabled={procesandoDevolucion} style={{ flex: 1, padding: '12px', border: 0, borderRadius: '7px', background: '#e2e8f0', fontWeight: 'bold' }}>Cancelar</button>
              <button onClick={confirmarDevolucion} disabled={procesandoDevolucion} style={{ flex: 2, padding: '12px', border: 0, borderRadius: '7px', background: modalDevolucion.tipo === 'Total' ? '#dc2626' : '#f59e0b', color: 'white', fontWeight: 'bold' }}>{procesandoDevolucion ? '⏳ Procesando...' : 'Confirmar operación'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' };
const inputStyle = { padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', backgroundColor: '#f8fafc', outline: 'none' };
const btnStyle = { padding: '10px 15px', borderRadius: '8px', border: 'none', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' };
